import { Fragment, useEffect, useMemo, useState } from 'react';
import { testiStampa } from '../../api/testiStampa';
import type { TestoStampaLetto } from '../../api/testiStampa';
import { anagrafiche } from '../../api/acquisti';
import type { PagamentoMago } from '../../api/acquisti';
import type { DettaglioPreventivo, TestiPreventivo, VoceLiberaLetta } from '../../api/preventivi';
import { StatoScrittura } from './StatoScrittura';
import { predefiniteDelLavoro } from './predefiniteDelLavoro';
import { SezioneChiudibile } from './SezioneChiudibile';
import { Icona } from '../../components/Icona';

/*  Cosa di questa offerta si stampa a parole.

    Le clausole non si riscrivono qui: si spuntano. Riscriverle è di chi ha il permesso
    sui testi di stampa, ed è un'altra schermata — la separazione è la richiesta della
    riunione, «fai in modo che non tutti possano scrivere», e vale la pena che si veda
    anche nell'interfaccia invece di scoprirla con un 403.

    La nota per il cliente invece è libera, ed è l'unica cosa scritta qui: è la frase che
    cambia da offerta a offerta — «la caldaia esistente resta di proprietà del
    committente», «i lavori partono a settimana concordata». Se fosse standard non
    sarebbe una nota, sarebbe una clausola.

    Su un preventivo nuovo le voci marcate «in partenza» arrivano già spuntate. Non è una
    comodità: sono quelle che devono uscire sempre, e lasciare che le spunti chi compila
    vuol dire che prima o poi un'offerta esce senza le esclusioni.
*/
export function TestiOfferta({
  preventivo,
  modificabile,
  onSalva,
}: {
  preventivo: DettaglioPreventivo;
  modificabile: boolean;
  onSalva: (testi: TestiPreventivo) => Promise<unknown>;
}) {
  const [disponibili, setDisponibili] = useState<TestoStampaLetto[] | null>(null);
  const [pagamenti, setPagamenti] = useState<PagamentoMago[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const [nota, setNota] = useState(preventivo.notaPerCliente ?? '');
  const [attenzione, setAttenzione] = useState(preventivo.allaCorteseAttenzione ?? '');
  const [lettera, setLettera] = useState(preventivo.letteraId ?? '');
  const [pagamento, setPagamento] = useState(preventivo.codicePagamentoMago ?? '');
  const [scelti, setScelti] = useState<string[]>(preventivo.testiScelti);
  const [libere, setLibere] = useState<VoceLiberaLetta[]>(preventivo.vociLibere);

  /*  DI CHE LAVORO E' QUESTA OFFERTA. Dall'08/10/2026 sta SUL DOCUMENTO e non piu'
      solo nella schermata: serve a far trovare gia' spuntate le clausole predefinite
      giuste — «l'amministratore spunta i predefiniti per categoria e poi il redattore
      se li trova gia' spuntati» — e un filtro che si sceglie mentre si guarda non
      poteva saperlo.

      Resta anche il filtro di lettura, perche' e' la stessa domanda: le voci che si
      guardano sono quelle del lavoro di cui parla l'offerta.  */
  const [categoria, setCategoria] = useState(preventivo.categoriaLavoro ?? '');

  useEffect(() => {
    testiStampa
      .elenco()
      .then(setDisponibili)
      .catch((e: Error) => setErrore(e.message));
  }, []);

  /*  Le condizioni di pagamento arrivano da Mago e non dai testi d'offerta: dal
      21/09/2026 le scrive l'amministrazione di la', una volta, per tutti i documenti.

      Se Mago non risponde la tendina resta vuota e il resto del pannello funziona: si
      salva la nota e si spuntano le clausole lo stesso. Un errore qui non deve
      diventare un pannello che non si apre.  */
  useEffect(() => {
    anagrafiche
      .pagamenti()
      .then(setPagamenti)
      .catch(() => setPagamenti([]));
  }, []);

  /*  LE PREDEFINITE DEL LAVORO DI CUI PARLA L'OFFERTA, piu' quelle che valgono sempre.

      Una clausola predefinita e' predefinita PER LA SUA CATEGORIA: le esclusioni
      elettriche non vanno spuntate da sole su un'offerta meccanica, e viceversa. Senza
      categoria sull'offerta restano solo quelle senza categoria — la garanzia, la
      validita' — che e' il comportamento di prima su un'offerta che nessuno ha ancora
      detto di che lavoro sia.

      Entrano solo su un'offerta che non ha ancora scelto niente. Su una già compilata
      no, nemmeno se l'elenco nel frattempo è cambiato: qualcuno aveva tolto quella
      spunta apposta, e rimettercela di nascosto le farebbe uscire una clausola che
      aveva deciso di non mandare.  */
  useEffect(() => {
    if (disponibili === null || preventivo.testiScelti.length > 0) return;

    setScelti(predefiniteDelLavoro(disponibili, preventivo.categoriaLavoro ?? ''));
  }, [disponibili, preventivo.testiScelti, preventivo.categoriaLavoro]);

  const tutteLeCondizioni = useMemo(
    () => (disponibili ?? []).filter((t) => t.gruppo === 'Condizioni'),
    [disponibili],
  );

  const categorie = useMemo(
    () => [...new Set(tutteLeCondizioni
      .map((t) => t.categoria)
      .filter((c): c is string => c !== null && c !== ''))].sort(),
    [tutteLeCondizioni],
  );

  /*  IL FILTRO PER CATEGORIA, chiesto in riunione il 01/10/2026: le esclusioni di un
      impianto elettrico non sono quelle di uno meccanico, e un elenco solo costringe a
      leggerle tutte per scartarne meta'.

      Le voci SENZA categoria restano sempre: valgono per tutti i lavori — la garanzia,
      la validita' dell'offerta — e nasconderle filtrando vorrebbe dire far spuntare un
      elenco a cui manca quello che c'e' su ogni offerta.  */
  const condizioni = useMemo(
    () => categoria === ''
      ? tutteLeCondizioni
      : tutteLeCondizioni.filter((t) => t.categoria === null || t.categoria === categoria),
    [tutteLeCondizioni, categoria],
  );

  /*  Quelle spuntate che il filtro sta nascondendo. Vanno dette: le spunte restano, e
      quindi quelle voci ESCONO IN OFFERTA lo stesso. Chi guarda un elenco filtrato e
      vede sei spunte non ha motivo di sospettare che in stampa ne finiscano otto.  */
  const spuntateNascoste = tutteLeCondizioni.filter(
    (t) => scelti.includes(t.id) && !condizioni.some((v) => v.id === t.id),
  ).length;
  const lettere = useMemo(
    () => (disponibili ?? []).filter((t) => t.gruppo === 'Lettera'),
    [disponibili],
  );
  const cambiato =
    nota !== (preventivo.notaPerCliente ?? '') ||
    attenzione !== (preventivo.allaCorteseAttenzione ?? '') ||
    lettera !== (preventivo.letteraId ?? '') ||
    pagamento !== (preventivo.codicePagamentoMago ?? '') ||
    categoria !== (preventivo.categoriaLavoro ?? '') ||
    scelti.length !== preventivo.testiScelti.length ||
    scelti.some((id) => !preventivo.testiScelti.includes(id)) ||
    JSON.stringify(libere) !== JSON.stringify(preventivo.vociLibere);

  const daiCampi = (): TestiPreventivo => ({
    notaPerCliente: nota.trim() === '' ? null : nota,
    allaCorteseAttenzione: attenzione.trim() === '' ? null : attenzione.trim(),
    letteraId: lettera === '' ? null : lettera,
    codicePagamentoMago: pagamento === '' ? null : pagamento,
    categoriaLavoro: categoria === '' ? null : categoria,
    testiScelti: scelti,
    vociLibere: libere,
  });

  /*  `modifiche` perche' una spunta e una tendina si salvano nel momento in cui si
      scelgono, e in quel momento lo stato di React non e' ancora cambiato: il valore
      nuovo arriva da qui invece che da una casella che sta per diventare un'altra cosa.  */
  const salva = (modifiche: Partial<TestiPreventivo> = {}) => {
    setInCorso(true);
    setErrore(null);

    onSalva({ ...daiCampi(), ...modifiche })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  /*  LA NOTA SI SCRIVE USCENDO DAL CAMPO e non a ogni tasto: a ogni tasto sarebbe una
      scrittura per lettera, e una frase a meta' non e' una frase che qualcuno ha scritto.

      Qui e' un'area di testo e non una casella, quindi una riga andata a capo non manda
      niente: si esce quando si e' finito, che e' il momento in cui quello che c'e'
      scritto e' quello che si intendeva.  */
  const salvaSeCambiato = () => {
    if (!modificabile || inCorso || !cambiato) return;

    salva();
  };

  /*  CAMBIARE IL LAVORO si salva subito, come ogni tendina. Su un'offerta ancora
      vergine — nessuna clausola spuntata — porta anche le predefinite del lavoro
      nuovo: e' il caso normale, si apre l'offerta, si dice di che impianto parla e le
      clausole ci sono gia'.

      Su un'offerta gia' compilata le spunte NON si toccano: quelle le ha decise
      qualcuno, e cambiarle sotto il naso di chi ha appena scelto il filtro sarebbe il
      modo di mandare al cliente una clausola che aveva tolto. Per quelle c'e' il
      bottone, che dice quante ne mette.  */
  const cambiaCategoria = (nuova: string) => {
    setCategoria(nuova);

    const vergine = scelti.length === 0;
    const nuoviScelti = vergine && disponibili !== null
      ? predefiniteDelLavoro(disponibili, nuova)
      : scelti;

    if (vergine) setScelti(nuoviScelti);

    salva({ categoriaLavoro: nuova === '' ? null : nuova, testiScelti: nuoviScelti });
  };

  /*  Le predefinite di questo lavoro che non sono spuntate. Si contano per dirlo sul
      bottone: «mettine 20» e «non c'e' niente da mettere» sono due risposte diverse, e
      un bottone che non fa niente e' peggio di un bottone che non c'e'.  */
  const mancanti = disponibili === null || categoria === ''
    ? []
    : predefiniteDelLavoro(disponibili, categoria).filter((id) => !scelti.includes(id));
  const daMettere = mancanti.length;

  const mettiLePredefinite = () => {
    const nuovi = [...scelti, ...mancanti];

    setScelti(nuovi);
    salva({ testiScelti: nuovi });
  };

  /*  Le voci scritte a mano si salvano tutte insieme, come le spunte: l'elenco intero
      sostituisce quello di prima.  */
  const cambiaLibere = (nuove: VoceLiberaLetta[]) => {
    setLibere(nuove);
    salva({ vociLibere: nuove });
  };

  /*  Quelle il cui titolo non si vede con il filtro scelto: escono in offerta lo stesso,
      e va detto per la stessa ragione delle spuntate nascoste.  */
  const libereNascoste = libere.filter(
    (v) => !condizioni.some((t) => t.id === v.titoloId && t.forma === 'Titolo'),
  ).length;

  const spunta = (id: string, acceso: boolean) => {
    const nuovi = acceso ? [...scelti, id] : scelti.filter((x) => x !== id);

    setScelti(nuovi);
    salva({ testiScelti: nuovi });
  };

  return (
    <SezioneChiudibile classe="testi-offerta" titolo="Testi dell'offerta" livello={2}>

      {errore !== null && <p className="errore">{errore}</p>}
      {disponibili === null && <p className="attesa">Lettura dei testi…</p>}

      {disponibili !== null && (
        <>
          {/*  A CHI VA QUESTA OFFERTA, sopra la nota: in un foglio stampato sta in
               cima, e qui sta nell'ordine in cui si legge.

               Campo libero e non una tendina dei referenti: l'anagrafica ne tiene uno
               solo, e «se hai 1000 referenti lo lasci vuoto, lo scrivi tu di volta in
               volta» (Andrea, 01/10/2026). Vuoto non e' un buco da riempire: la maggior
               parte delle offerte va all'azienda e basta, e in stampa la riga non esce
               affatto invece di uscire monca.  */}
          <label className="campo">
            Alla cortese attenzione di
            <input
              value={attenzione}
              onChange={(e) => setAttenzione(e.target.value)}
              onBlur={salvaSeCambiato}
              maxLength={128}
              disabled={!modificabile}
              placeholder="Il nome di chi riceve l'offerta. Vuoto: va all'azienda."
            />
          </label>

          <label className="campo">
            Nota per il cliente
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              onBlur={salvaSeCambiato}
              rows={3}
              maxLength={2000}
              disabled={!modificabile}
              placeholder="La frase che vale solo per questa offerta."
            />
          </label>

          <div className="riga-campi">
            <label>
              Lettera di accompagnamento
              <select
                value={lettera}
                onChange={(e) => {
                  setLettera(e.target.value);
                  salva({ letteraId: e.target.value === '' ? null : e.target.value });
                }}
                disabled={!modificabile}
              >
                <option value="">Nessuna</option>
                {lettere.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.testo.slice(0, 70)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Modalità di pagamento
              <select
                value={pagamento}
                onChange={(e) => {
                  setPagamento(e.target.value);
                  salva({ codicePagamentoMago: e.target.value === '' ? null : e.target.value });
                }}
                disabled={!modificabile}
              >
                <option value="">Nessuna</option>
                {/*  Quella gia' scelta resta in elenco anche se in Mago l'hanno
                     disabilitata: togliergliela sotto gli occhi cambierebbe in
                     silenzio cosa promette un'offerta.  */}
                {pagamento !== '' && !pagamenti.some((m) => m.codice === pagamento) && (
                  <option value={pagamento}>{preventivo.pagamento ?? pagamento}</option>
                )}
                {pagamenti.map((m) => (
                  <option key={m.codice} value={m.codice}>
                    {m.descrizione}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <h3>Condizioni, oneri ed esclusioni</h3>

          {/*  IL TIPO DI LAVORO, che e' insieme il filtro e una scelta del documento.
               Cambiandolo su un'offerta ancora vergine arrivano le sue predefinite gia'
               spuntate; su una gia' compilata no, e il bottone qui sotto le aggiunge a
               mano — di nascosto rimetterebbe in offerta una clausola che qualcuno
               aveva tolto apposta.  */}
          {categorie.length > 0 && (
            <p className="filtro-categorie">
              <label>
                Lavoro
                <select
                  value={categoria}
                  disabled={!modificabile || inCorso}
                  onChange={(e) => cambiaCategoria(e.target.value)}
                >
                  <option value="">non deciso — tutte le voci</option>
                  {categorie.map((c) => (
                    <option key={c} value={c}>
                      {c} — e quelle che valgono sempre
                    </option>
                  ))}
                </select>
              </label>
              {modificabile && categoria !== '' && daMettere > 0 && (
                <button
                  type="button"
                  className="salva-riga"
                  disabled={inCorso}
                  onClick={mettiLePredefinite}
                >
                  {daMettere === 1
                    ? 'Metti la predefinita che manca'
                    : `Metti le ${daMettere} predefinite che mancano`}
                </button>
              )}
              {spuntateNascoste > 0 && (
                <em className="nota">
                  {spuntateNascoste === 1
                    ? 'Una voce spuntata non si vede con questo filtro, e in offerta esce lo stesso.'
                    : `${spuntateNascoste} voci spuntate non si vedono con questo filtro, e in offerta escono lo stesso.`}
                </em>
              )}
            </p>
          )}

          {condizioni.length === 0 && (
            <p className="nota">
              Nessuna voce in elenco. Si scrivono dalla schermata dei testi dell'offerta.
            </p>
          )}

          {libereNascoste > 0 && (
            <p className="nota">
              {libereNascoste === 1
                ? 'Una voce scritta a mano sta sotto un titolo che con questo filtro non si vede, e in offerta esce lo stesso.'
                : `${libereNascoste} voci scritte a mano stanno sotto titoli che con questo filtro non si vedono, e in offerta escono lo stesso.`}
            </p>
          )}

          <ul className="spunte-condizioni">
            {condizioni.map((voce, i) => (
              <Fragment key={voce.id}>
              <li className={voce.forma === 'Titolo' ? 'titolo' : ''}>
                {voce.forma === 'Titolo' ? (
                  /*  Un titolo apre una sezione e non si spunta: esce se sotto c'è
                      qualcosa. Mostrarlo con una casella vorrebbe dire far scegliere di
                      stampare un titolo su niente.  */
                  <strong>{voce.testo}</strong>
                ) : (
                  <label>
                    <input
                      type="checkbox"
                      checked={scelti.includes(voce.id)}
                      onChange={(e) => spunta(voce.id, e.target.checked)}
                      disabled={!modificabile}
                    />
                    <span>{voce.testo}</span>
                  </label>
                )}
              </li>
              {/*  In fondo a ogni sezione — prima del titolo dopo, o alla fine — le voci
                   scritte a mano per questa offerta e il «+» per aggiungerne (16/10/2026).  */}
              {(i === condizioni.length - 1 || condizioni[i + 1]!.forma === 'Titolo') &&
                titoloDi(condizioni, i) !== null && (
                  <VociLibereDellaSezione
                    titolo={titoloDi(condizioni, i)!}
                    libere={libere}
                    modificabile={modificabile && !inCorso}
                    onCambia={cambiaLibere}
                  />
                )}
              </Fragment>
            ))}
          </ul>

          {modificabile && <StatoScrittura inCorso={inCorso} allineato={!cambiato} />}
        </>
      )}
    </SezioneChiudibile>
  );
}

/** Il titolo della sezione in cui sta la voce in posizione `i`, o null se sta prima di ogni titolo. */
function titoloDi(elenco: TestoStampaLetto[], i: number): TestoStampaLetto | null {
  for (let j = i; j >= 0; j--) {
    if (elenco[j]!.forma === 'Titolo') return elenco[j]!;
  }
  return null;
}

/*  LE VOCI SCRITTE A MANO DI UNA SEZIONE, e il «+» per aggiungerne. Chiesto da Galizzi
    il 16/10/2026: «e' solo fornitura, sono esclusi gli scoperti del DC» vale per
    quell'offerta e basta, e scritto nell'elenco generale lo vedrebbero tutti, ogni
    volta. Qui sta sul documento, in fondo alla sua sezione, e in stampa esce li'.

    Si corregge nel campo e si salva uscendo, come la nota; si toglie con la X. Il
    campo per aggiungere accetta anche un testo incollato lungo: e' il caso tipico,
    una voce da capitolato copiata da un altro documento.  */
function VociLibereDellaSezione({
  titolo,
  libere,
  modificabile,
  onCambia,
}: {
  titolo: TestoStampaLetto;
  libere: VoceLiberaLetta[];
  modificabile: boolean;
  onCambia: (nuove: VoceLiberaLetta[]) => void;
}) {
  const [nuova, setNuova] = useState('');
  const mie = libere
    .map((v, indice) => ({ v, indice }))
    .filter(({ v }) => v.titoloId === titolo.id);

  if (!modificabile && mie.length === 0) return null;

  const aggiungi = () => {
    const testo = nuova.trim();
    if (testo === '') return;

    onCambia([...libere, { titoloId: titolo.id, testo }]);
    setNuova('');
  };

  return (
    <li className="voci-libere">
      {mie.length > 0 && (
        <ul>
          {mie.map(({ v, indice }) => (
            <VoceLibera
              key={`${indice}-${v.testo}`}
              voce={v}
              titolo={titolo.testo}
              modificabile={modificabile}
              onCorreggi={(testo) =>
                onCambia(libere.map((x, k) => (k === indice ? { ...x, testo } : x)))
              }
              onTogli={() => onCambia(libere.filter((_, k) => k !== indice))}
            />
          ))}
        </ul>
      )}
      {modificabile && (
        <p className="aggiungi-libera">
          <textarea
            rows={1}
            aria-label={`Voce solo per questa offerta sotto ${titolo.testo}`}
            placeholder={`+ voce solo per questa offerta, sotto «${titolo.testo}»`}
            value={nuova}
            onChange={(e) => setNuova(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                aggiungi();
              }
            }}
          />
          <button type="button" className="salva-riga" disabled={nuova.trim() === ''} onClick={aggiungi}>
            Aggiungi
          </button>
        </p>
      )}
    </li>
  );
}

function VoceLibera({
  voce,
  titolo,
  modificabile,
  onCorreggi,
  onTogli,
}: {
  voce: VoceLiberaLetta;
  titolo: string;
  modificabile: boolean;
  onCorreggi: (testo: string) => void;
  onTogli: () => void;
}) {
  const [testo, setTesto] = useState(voce.testo);

  if (!modificabile) {
    return (
      <li>
        <span className="libera">{voce.testo}</span>
      </li>
    );
  }

  return (
    <li>
      <textarea
        rows={Math.min(6, Math.max(1, Math.ceil(testo.length / 90)))}
        aria-label={`Voce scritta a mano sotto ${titolo}`}
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
        onBlur={() => {
          const pulito = testo.trim();
          if (pulito === '') onTogli();
          else if (pulito !== voce.testo) onCorreggi(pulito);
        }}
      />
      <button
        type="button"
        className="chiudi"
        aria-label={`Togli la voce «${voce.testo}»`}
        title="Togli la voce"
        onClick={onTogli}
      >
        <Icona nome="chiudi" />
      </button>
    </li>
  );
}
